import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/lib/site";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#1F3A2D] text-[#F6F4F0] relative overflow-hidden"
    >
      {/* Big CTA */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 grid lg:grid-cols-[1.4fr_1fr] gap-12 items-end">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#C8B79A] mb-6">
              Let's talk trade
            </p>
            <h2 className="font-serif-display text-4xl sm:text-6xl leading-[1.05] tracking-tight">
              Sourcing partners across <em className="text-[#D8A48F] not-italic">six continents.</em>
            </h2>
          </div>
          <div className="flex flex-col gap-4 lg:items-end">
            <p className="text-sm text-[#D6D2C8] max-w-sm">
              Whether you need a single container of Red Lentils or large-volume contracts for Pulses,
              Oilseeds or Cotton — we move commodities reliably, on spec, and on time.
            </p>
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#1A1A1A] px-6 py-3.5 text-sm font-semibold hover:bg-[#D8A48F] hover:text-[#1A1A1A] transition-colors"
            >
              Start a conversation →
            </Link>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-1">
          <div className="mb-6 inline-block bg-[#FDFBF7] rounded-xl px-5 py-3">
            <img
              src={COMPANY.logo}
              alt="Farmico logo"
              className="h-16 w-auto object-contain"
            />
          </div>
          <p className="text-sm text-[#F6F4F0]/90 leading-relaxed font-semibold">
            Farmico International Trading FZE
          </p>
          <p className="text-sm text-[#F6F4F0]/90 leading-relaxed font-semibold">
            Farmico Proteins FZCO
          </p>
          <p className="mt-4 text-sm text-[#C8B79A] leading-relaxed">
            Global multi-commodity trading &amp; international brokerage house for pulses &amp;
            oilseeds.
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8B79A] mb-4">Explore</p>
          <ul className="space-y-3 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-[#F6F4F0]/85 hover:text-white link-underline"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8B79A] mb-4">Contact</p>
          <ul className="space-y-4 text-sm text-[#F6F4F0]/90">
            <li className="flex gap-3">
              <MapPin className="h-4 w-4 mt-1 shrink-0 text-[#D8A48F]" />
              <a
                href={COMPANY.mapsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-address"
                className="hover:text-white"
              >
                {COMPANY.address}
              </a>
            </li>
            <li className="flex gap-3 items-start">
              <Phone className="h-4 w-4 mt-1 shrink-0 text-[#D8A48F]" />
              <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} data-testid="footer-phone" className="hover:text-white">
                {COMPANY.phone}
              </a>
            </li>
            <li className="flex gap-3 items-start">
              <MessageCircle className="h-4 w-4 mt-1 shrink-0 text-[#D8A48F]" />
              <a
                href={`https://wa.me/${COMPANY.whatsapp.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp"
                className="hover:text-white"
              >
                WhatsApp · {COMPANY.whatsapp}
              </a>
            </li>
            <li className="flex gap-3 items-start">
              <Mail className="h-4 w-4 mt-1 shrink-0 text-[#D8A48F]" />
              <a href={`mailto:${COMPANY.email}`} data-testid="footer-email" className="hover:text-white">
                {COMPANY.email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-[#C8B79A]">
          <p>© {new Date().getFullYear()} {COMPANY.groupName}. All rights reserved.</p>
          <p>Registered in Dubai, United Arab Emirates · Multi-Commodity Traders & Brokers</p>
        </div>
      </div>
    </footer>
  );
}
